/**
 * Périodes de paie des livreurs, déduites des deux jours de paie du réglage
 * (`platform_config.payout_day_1` / `payout_day_2`, fixés au 1er et au 16 par la
 * migration 0048).
 *
 * Règle : un jour de paie OUVRE une période, qui court jusqu'à la veille du jour de
 * paie suivant. Avec 1 et 16 : du 1er au 15, puis du 16 à la fin du mois. Avec 5 et
 * 20 : du 5 au 19, puis du 20 au 4 du mois suivant.
 *
 * Les dates sont des chaînes `AAAA-MM-JJ`, calculées en UTC : la journée de Bamako
 * est celle d'UTC (pas d'heure d'été), et une date « civile » ne doit jamais dépendre
 * du fuseau du navigateur de l'opérateur.
 */

export interface PeriodePaie {
  /** Premier jour compris. */
  debut: string
  /** Dernier jour compris. */
  fin: string
  /** « 1er au 15 octobre 2026 ». */
  libelle: string
  /** Vrai si le dernier jour est passé : la période est complète, donc payable. */
  terminee: boolean
}

const MOIS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
]

const MOIS_COURTS = [
  'janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
  'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.',
]

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/

const enMs = (iso: string): number => {
  const m = ISO.exec(iso)
  if (!m) throw new Error(`Date invalide : ${iso}`)
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
}
const enIso = (ms: number): string => new Date(ms).toISOString().slice(0, 10)
const JOUR = 86_400_000

const joursDuMois = (annee: number, mois0: number): number => new Date(Date.UTC(annee, mois0 + 1, 0)).getUTCDate()

/** Les deux jours, triés ; `null` s'ils ne forment pas un réglage exploitable. */
export function joursDePaieValides(jour1: number, jour2: number): [number, number] | null {
  const ok = (j: number) => Number.isInteger(j) && j >= 1 && j <= 31
  if (!ok(jour1) || !ok(jour2) || jour1 === jour2) return null
  return jour1 < jour2 ? [jour1, jour2] : [jour2, jour1]
}

/** « 1er au 15 octobre 2026 » ; sur deux mois : « 16 sept. au 5 oct. 2026 ». */
export function libellePeriode(debut: string, fin: string): string {
  const d = new Date(enMs(debut))
  const f = new Date(enMs(fin))
  const jour = (x: Date) => (x.getUTCDate() === 1 ? '1er' : String(x.getUTCDate()))
  const annee = f.getUTCFullYear()
  if (d.getUTCMonth() === f.getUTCMonth() && d.getUTCFullYear() === annee) {
    return `${jour(d)} au ${jour(f)} ${MOIS[f.getUTCMonth()]} ${annee}`
  }
  const court = (x: Date) => MOIS_COURTS[x.getUTCMonth()]!
  const anneeDebut = d.getUTCFullYear() === annee ? '' : ` ${d.getUTCFullYear()}`
  return `${jour(d)} ${court(d)}${anneeDebut} au ${jour(f)} ${court(f)} ${annee}`
}

/**
 * Les périodes les plus récentes, la plus récente d'abord : celle en cours (non
 * terminée), puis les périodes closes. `[]` si le réglage est inexploitable.
 *
 * @param aujourdhui `AAAA-MM-JJ`
 * @param nombre     combien de périodes rendre (en cours comprise)
 */
export function periodesDePaie(
  jour1: number,
  jour2: number,
  aujourdhui: string,
  nombre = 6,
): PeriodePaie[] {
  const jours = joursDePaieValides(jour1, jour2)
  if (!jours) return []
  const auj = enMs(aujourdhui)
  const t = new Date(auj)
  const annee0 = t.getUTCFullYear()
  const mois0 = t.getUTCMonth()

  // Les débuts de période, de quelques mois avant à un mois après aujourd'hui.
  const marge = Math.ceil(nombre / 2) + 2
  const debuts = new Set<number>()
  for (let k = -marge; k <= 1; k++) {
    const ref = new Date(Date.UTC(annee0, mois0 + k, 1))
    const a = ref.getUTCFullYear()
    const m = ref.getUTCMonth()
    for (const j of jours) debuts.add(Date.UTC(a, m, Math.min(j, joursDuMois(a, m))))
  }
  const tries = [...debuts].sort((x, y) => x - y)

  const periodes: PeriodePaie[] = []
  for (let i = 0; i + 1 < tries.length; i++) {
    const debut = tries[i]!
    if (debut > auj) break // une période future n'existe pas encore
    const fin = tries[i + 1]! - JOUR
    periodes.push({
      debut: enIso(debut),
      fin: enIso(fin),
      libelle: libellePeriode(enIso(debut), enIso(fin)),
      terminee: fin < auj,
    })
  }
  return periodes.reverse().slice(0, nombre)
}

/** La période à payer maintenant : la plus récente dont tous les jours sont passés. */
export function periodeAPayer(periodes: PeriodePaie[]): PeriodePaie | null {
  return periodes.find((p) => p.terminee) ?? null
}
